<template>
  <v-container class="modern-financials-container" fluid ma-0 pa-0>
    <!-- Modern Header -->
    <div class="modern-financials-header">
      <div class="header-background">
        <div class="header-circle header-circle-1"></div>
        <div class="header-circle header-circle-2"></div>
        <div class="header-circle header-circle-3"></div>
      </div>
      <div class="header-content-wrapper">
        <div class="header-icon-wrapper">
          <v-icon size="56" color="white">mdi-chart-line</v-icon>
        </div>
        <div class="header-text-content">
          <h1 class="modern-financials-title">Financial Reports</h1>
          <p class="modern-financials-subtitle">
            <v-icon small color="white" class="mr-1">mdi-file-document</v-icon>
            Annual Financial Reports & Transparency
          </p>
        </div>
      </div>
    </div>

    <v-row class="ma-0 px-4 py-6" justify="center">
      <v-col cols="12" md="11" lg="10">
        <!-- Info Card -->
        <v-card class="modern-info-card mb-6" elevation="0">
          <div class="info-card-header">
            <div class="info-icon-wrapper">
              <v-icon color="white" size="32">mdi-information</v-icon>
            </div>
            <div class="info-header-text">
              <h2 class="info-title">Annual Financial Reports (AFR)</h2>
              <p class="info-description">
                The Annual Financial Report is the unaudited data prepared by the Charlotte Tamil Sangam.
                It includes revenue from Membership fee collection/Donations and expenditures detailed by
                category (the purpose of an expenditure) and object (what was purchased or the service obtained).
                The financial data would be submitted to Internal Revenue System (IRS) during tax filing every year.
              </p>
            </div>
          </div>
        </v-card>

        <!-- Stats Cards -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="3" class="pa-2">
            <v-card class="modern-stats-card revenue-card" elevation="0">
              <div class="stats-card-content">
                <div class="stats-icon-wrapper revenue-icon">
                  <v-icon color="white" size="32">mdi-cash-multiple</v-icon>
                </div>
                <div class="stats-info">
                  <div class="stats-label">Revenue</div>
                  <div class="stats-sublabel">Membership & Donations</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="pa-2">
            <v-card class="modern-stats-card expense-card" elevation="0">
              <div class="stats-card-content">
                <div class="stats-icon-wrapper expense-icon">
                  <v-icon color="white" size="32">mdi-receipt</v-icon>
                </div>
                <div class="stats-info">
                  <div class="stats-label">Expenditures</div>
                  <div class="stats-sublabel">Categorized Expenses</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="pa-2">
            <v-card class="modern-stats-card reports-card" elevation="0">
              <div class="stats-card-content">
                <div class="stats-icon-wrapper reports-icon">
                  <v-icon color="white" size="32">mdi-file-chart</v-icon>
                </div>
                <div class="stats-info">
                  <div class="stats-label">{{ items[0].children.length }} Years</div>
                  <div class="stats-sublabel">Available Reports</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="pa-2">
            <v-card class="modern-stats-card irs-card" elevation="0">
              <div class="stats-card-content">
                <div class="stats-icon-wrapper irs-icon">
                  <v-icon color="white" size="32">mdi-shield-check</v-icon>
                </div>
                <div class="stats-info">
                  <div class="stats-label">IRS Filing</div>
                  <div class="stats-sublabel">Tax Compliance</div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Main Content Card -->
        <v-card class="modern-content-card" elevation="0">
          <div class="content-section-header">
            <div class="section-icon-badge">
              <v-icon color="white" size="24">mdi-folder-open</v-icon>
            </div>
            <h2 class="content-section-title">Browse Financial Reports</h2>
          </div>

          <v-row class="ma-0">
            <!-- Treeview Column -->
            <v-col cols="12" md="4" lg="3" class="treeview-column pa-0">
              <div class="treeview-wrapper">
                <div class="treeview-header">
                  <v-icon color="#667eea" class="mr-2">mdi-calendar-range</v-icon>
                  <span class="treeview-header-text">Select Year</span>
                </div>
                <v-treeview
                  v-model="selection"
                  :open="open"
                  :items="items"
                  selectable
                  return-object
                  open-all
                  item-key="name"
                  class="modern-treeview"
                  color="#667eea"
                  selected-color="#667eea"
                >
                  <template v-slot:prepend="{ item, open }">
                    <div class="tree-icon-wrapper" :class="{ 'folder-icon': !item.file, 'file-icon': item.file }">
                      <v-icon v-if="!item.file" :color="open ? '#667eea' : '#64748b'">
                        {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                      </v-icon>
                      <v-icon v-else color="#f44336">{{ files[item.file] }}</v-icon>
                    </div>
                  </template>
                  <template v-slot:label="{ item }">
                    <span class="tree-label" :class="{ 'folder-label': !item.file, 'file-label': item.file }">
                      {{ item.name }}
                    </span>
                  </template>
                </v-treeview>
              </div>
            </v-col>

            <v-divider vertical class="modern-divider"></v-divider>

            <!-- Content Column -->
            <v-col cols="12" md="8" lg="9" class="content-column pa-0">
              <div class="content-wrapper">
                <div v-if="selection.length === 0" class="empty-state">
                  <div class="empty-icon-wrapper">
                    <v-icon size="64" color="#cbd5e1">mdi-file-document-outline</v-icon>
                  </div>
                  <h3 class="empty-title">No Report Selected</h3>
                  <p class="empty-description">Please select a year from the list to view the financial report</p>
                </div>
                <div v-else class="selected-reports">
                  <div v-for="node in selection" :key="node.name" class="report-item">
                    <v-card class="report-card" elevation="0">
                      <div class="report-card-content">
                        <div class="report-icon-wrapper">
                          <v-icon color="white" size="40">mdi-file-pdf</v-icon>
                        </div>
                        <div class="report-info">
                          <h3 class="report-year">{{ node.name }} Financial Report</h3>
                          <p class="report-description">Annual Financial Report for the year {{ node.name }}</p>
                        </div>
                        <div class="report-action">
                          <v-btn
                            :href="node.path"
                            target="_blank"
                            class="modern-view-btn"
                            elevation="0"
                          >
                            <v-icon left>mdi-open-in-new</v-icon>
                            View Report
                          </v-btn>
                        </div>
                      </div>
                    </v-card>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  export default {
    data: () => ({
      open: ['ByYear'],
      files: {
        pdf: 'mdi-file-pdf'
      },
      selection: [],
      items: [
        {
          name: 'ByYear',
          children: [
            { name: '2024', file: 'pdf', path: 'https://cltts-docs.s3.amazonaws.com/Financial/cltts_finance_2024.pdf' },
            { name: '2023', file: 'pdf', path: 'https://cltts-docs.s3.amazonaws.com/Financial/cltts_finance_2023.pdf' },
            { name: '2022', file: 'pdf', path: 'https://cltts-docs.s3.amazonaws.com/Financial/cltts_finance_2022.pdf' },
            { name: '2021', file: 'pdf', path: 'https://cltts-docs.s3.amazonaws.com/Financial/cltts_finance_2021.pdf' },
            { name: '2020', file: 'pdf', path: 'https://clttsdatabase.s3.amazonaws.com/financials/2020-Balance-Sheet.pdf' },
            { name: '2019', file: 'pdf', path: 'https://clttsdatabase.s3.amazonaws.com/financials/2019-Balance-Sheet.pdf' },
            { name: '2018', file: 'pdf', path: 'https://clttsdatabase.s3.amazonaws.com/financials/2018-Balance-Sheet.pdf' },
            { name: '2017', file: 'pdf', path: 'https://clttsdatabase.s3.amazonaws.com/financials/2017-Balance-Sheet.pdf' },
            { name: '2016', file: 'pdf', path: 'https://clttsdatabase.s3.amazonaws.com/financials/2016Balancesheet.pdf' },
            { name: '2015', file: 'pdf', path: 'https://clttsdatabase.s3.amazonaws.com/financials/2015Balancesheet.pdf' },
            { name: '2014', file: 'pdf', path: 'https://clttsdatabase.s3.amazonaws.com/financials/2014-Balance-Sheet.pdf' },
          ]
        }
      ],
    }),
    methods: {
      getDetails(detail) {
        var html = detail
        if(detail.startsWith('http')) {
          html = detail.replace(html, '<a href="' + html + '" target="_blank">' + 'Click to View Financial Report' + '</a>')
          return html
        }
        return html
      },
    }
  }
</script>

<style scoped lang="scss">
* {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
}

/* Container */
.modern-financials-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%) !important;
  min-height: 100vh !important;
}

/* Modern Header */
.modern-financials-header {
  position: relative !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  padding: 50px 24px !important;
  overflow: hidden !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
}

.header-background {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  overflow: hidden !important;
}

.header-circle {
  position: absolute !important;
  border-radius: 50% !important;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%) !important;
}

.header-circle-1 {
  width: 400px !important;
  height: 400px !important;
  top: -200px !important;
  right: -100px !important;
}

.header-circle-2 {
  width: 300px !important;
  height: 300px !important;
  bottom: -150px !important;
  left: -50px !important;
}

.header-circle-3 {
  width: 200px !important;
  height: 200px !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  opacity: 0.5 !important;
}

.header-content-wrapper {
  position: relative !important;
  z-index: 2 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 24px !important;
  flex-wrap: wrap !important;
}

.header-icon-wrapper {
  width: 80px !important;
  height: 80px !important;
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(20px) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.15),
    inset 0 2px 4px rgba(255, 255, 255, 0.3) !important;
}

.header-text-content {
  text-align: center !important;
}

.modern-financials-title {
  font-size: 42px !important;
  font-weight: 800 !important;
  color: white !important;
  margin: 0 !important;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  letter-spacing: -0.5px !important;
}

.modern-financials-subtitle {
  font-size: 16px !important;
  color: rgba(255, 255, 255, 0.95) !important;
  margin: 8px 0 0 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 4px !important;
}

/* Info Card */
.modern-info-card {
  border-radius: 18px !important;
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.9) 100%) !important;
  backdrop-filter: blur(30px) !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.08),
    inset 0 2px 4px rgba(255, 255, 255, 1) !important;
  overflow: hidden !important;
  position: relative !important;
}

.modern-info-card::before {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  height: 5px !important;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%) !important;
  background-size: 200% 100% !important;
  animation: gradientFlow 3s ease infinite !important;
}

.info-card-header {
  padding: 32px !important;
  display: flex !important;
  align-items: flex-start !important;
  gap: 20px !important;
}

.info-icon-wrapper {
  width: 64px !important;
  height: 64px !important;
  border-radius: 14px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow:
    0 8px 20px rgba(102, 126, 234, 0.4),
    inset 0 2px 4px rgba(255, 255, 255, 0.3) !important;
  flex-shrink: 0 !important;
}

.info-header-text {
  flex: 1 !important;
}

.info-title {
  font-size: 24px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  margin: 0 0 12px 0 !important;
  line-height: 1.2 !important;
}

.info-description {
  font-size: 15px !important;
  color: #64748b !important;
  line-height: 1.7 !important;
  margin: 0 !important;
}

/* Stats Cards */
.modern-stats-card {
  border-radius: 16px !important;
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.9) 100%) !important;
  backdrop-filter: blur(30px) !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.1),
    0 4px 12px rgba(0, 0, 0, 0.06),
    inset 0 2px 4px rgba(255, 255, 255, 1) !important;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative !important;
  overflow: hidden !important;
}

.modern-stats-card::before {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  height: 4px !important;
  background-size: 200% 100% !important;
  animation: gradientFlow 3s ease infinite !important;
}

.revenue-card::before {
  background: linear-gradient(90deg, #4CAF50 0%, #388E3C 50%, #81C784 100%) !important;
}

.expense-card::before {
  background: linear-gradient(90deg, #FF9800 0%, #F57C00 50%, #FFB74D 100%) !important;
}

.reports-card::before {
  background: linear-gradient(90deg, #2196F3 0%, #1976D2 50%, #64B5F6 100%) !important;
}

.irs-card::before {
  background: linear-gradient(90deg, #9C27B0 0%, #7B1FA2 50%, #BA68C8 100%) !important;
}

.modern-stats-card:hover {
  transform: translateY(-8px) scale(1.03) !important;
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.15),
    0 8px 16px rgba(0, 0, 0, 0.1),
    inset 0 2px 4px rgba(255, 255, 255, 1) !important;
}

.stats-card-content {
  padding: 20px !important;
  display: flex !important;
  align-items: center !important;
  gap: 16px !important;
}

.stats-icon-wrapper {
  width: 56px !important;
  height: 56px !important;
  border-radius: 14px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.15),
    inset 0 1px 2px rgba(255, 255, 255, 0.3) !important;
  position: relative !important;
  overflow: hidden !important;
  flex-shrink: 0 !important;
}

.stats-icon-wrapper::before {
  content: '' !important;
  position: absolute !important;
  top: -50% !important;
  left: -50% !important;
  width: 200% !important;
  height: 200% !important;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent) !important;
  transform: rotate(45deg) !important;
  animation: shimmer 3s infinite !important;
}

.revenue-icon {
  background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%) !important;
}

.expense-icon {
  background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%) !important;
}

.reports-icon {
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%) !important;
}

.irs-icon {
  background: linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%) !important;
}

.stats-info {
  flex: 1 !important;
}

.stats-label {
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  line-height: 1.2 !important;
  margin-bottom: 4px !important;
}

.stats-sublabel {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #64748b !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

/* Content Card */
.modern-content-card {
  border-radius: 18px !important;
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.9) 100%) !important;
  backdrop-filter: blur(30px) !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.08),
    inset 0 2px 4px rgba(255, 255, 255, 1) !important;
  overflow: hidden !important;
  position: relative !important;
}

.modern-content-card::before {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  height: 5px !important;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%) !important;
  background-size: 200% 100% !important;
  animation: gradientFlow 3s ease infinite !important;
}

.content-section-header {
  padding: 24px 32px !important;
  display: flex !important;
  align-items: center !important;
  gap: 16px !important;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1) !important;
}

.section-icon-badge {
  width: 48px !important;
  height: 48px !important;
  border-radius: 12px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow:
    0 4px 12px rgba(102, 126, 234, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.3) !important;
}

.content-section-title {
  font-size: 24px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  margin: 0 !important;
}

/* Treeview Column */
.treeview-column {
  background: rgba(102, 126, 234, 0.03) !important;
  border-right: 2px solid rgba(102, 126, 234, 0.1) !important;
}

.treeview-wrapper {
  padding: 24px !important;
}

.treeview-header {
  display: flex !important;
  align-items: center !important;
  padding: 12px 16px !important;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
  border-radius: 10px !important;
  margin-bottom: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.2) !important;
}

.treeview-header-text {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #667eea !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.modern-treeview::v-deep .v-treeview-node__root {
  padding: 8px 12px !important;
  border-radius: 10px !important;
  margin-bottom: 6px !important;
  transition: all 0.3s ease !important;
}

.modern-treeview::v-deep .v-treeview-node__root:hover {
  background: rgba(102, 126, 234, 0.08) !important;
}

.modern-treeview::v-deep .v-treeview-node--active {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%) !important;
  border-radius: 10px !important;
}

.modern-treeview::v-deep .v-treeview-node__checkbox {
  color: #667eea !important;
}

.tree-icon-wrapper {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin-right: 8px !important;
}

.tree-label {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #1f2937 !important;
}

.folder-label {
  color: #667eea !important;
  font-weight: 700 !important;
}

.file-label {
  color: #4b5563 !important;
}

/* Modern Divider */
.modern-divider {
  border-color: rgba(102, 126, 234, 0.15) !important;
  border-width: 2px !important;
}

/* Content Column */
.content-column {
  min-height: 400px !important;
}

.content-wrapper {
  padding: 32px !important;
}

/* Empty State */
.empty-state {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 60px 24px !important;
  text-align: center !important;
}

.empty-icon-wrapper {
  width: 120px !important;
  height: 120px !important;
  border-radius: 50% !important;
  background: linear-gradient(135deg, rgba(203, 213, 225, 0.2) 0%, rgba(148, 163, 184, 0.2) 100%) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin-bottom: 24px !important;
}

.empty-title {
  font-size: 20px !important;
  font-weight: 700 !important;
  color: #64748b !important;
  margin: 0 0 8px 0 !important;
}

.empty-description {
  font-size: 14px !important;
  color: #94a3b8 !important;
  margin: 0 !important;
}

/* Selected Reports */
.selected-reports {
  display: flex !important;
  flex-direction: column !important;
  gap: 16px !important;
}

.report-item {
  width: 100% !important;
}

.report-card {
  border-radius: 14px !important;
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(248, 250, 252, 0.85) 100%) !important;
  backdrop-filter: blur(20px) !important;
  border: 2px solid rgba(102, 126, 234, 0.2) !important;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.08),
    inset 0 1px 2px rgba(255, 255, 255, 1) !important;
  transition: all 0.3s ease !important;
  overflow: hidden !important;
  position: relative !important;
}

.report-card::before {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  height: 3px !important;
  background: linear-gradient(90deg, #f44336 0%, #d32f2f 50%, #EF5350 100%) !important;
  background-size: 200% 100% !important;
  animation: gradientFlow 3s ease infinite !important;
}

.report-card:hover {
  transform: translateY(-4px) !important;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.12),
    inset 0 1px 2px rgba(255, 255, 255, 1) !important;
  border-color: rgba(102, 126, 234, 0.3) !important;
}

.report-card-content {
  padding: 24px !important;
  display: flex !important;
  align-items: center !important;
  gap: 20px !important;
  flex-wrap: wrap !important;
}

.report-icon-wrapper {
  width: 72px !important;
  height: 72px !important;
  border-radius: 14px !important;
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow:
    0 6px 16px rgba(244, 67, 54, 0.4),
    inset 0 2px 4px rgba(255, 255, 255, 0.3) !important;
  flex-shrink: 0 !important;
}

.report-info {
  flex: 1 !important;
  min-width: 200px !important;
}

.report-year {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  margin: 0 0 6px 0 !important;
  line-height: 1.2 !important;
}

.report-description {
  font-size: 14px !important;
  color: #64748b !important;
  margin: 0 !important;
  line-height: 1.4 !important;
}

.report-action {
  flex-shrink: 0 !important;
}

.modern-view-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  box-shadow:
    0 4px 12px rgba(102, 126, 234, 0.3),
    inset 0 1px 2px rgba(255, 255, 255, 0.2) !important;
  transition: all 0.3s ease !important;
}

.modern-view-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow:
    0 6px 16px rgba(102, 126, 234, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.2) !important;
}

/* Animations */
@keyframes gradientFlow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

/* Responsive Design */
@media (max-width: 960px) {
  .modern-financials-title {
    font-size: 36px !important;
  }

  .modern-financials-subtitle {
    font-size: 14px !important;
  }

  .info-title,
  .content-section-title {
    font-size: 20px !important;
  }

  .info-description {
    font-size: 14px !important;
  }

  .info-card-header {
    padding: 24px !important;
  }

  .content-section-header {
    padding: 20px 24px !important;
  }

  .treeview-column {
    border-right: none !important;
    border-bottom: 2px solid rgba(102, 126, 234, 0.1) !important;
  }

  .content-wrapper {
    padding: 24px !important;
  }

  .report-card-content {
    padding: 20px !important;
  }

  .report-year {
    font-size: 16px !important;
  }

  .report-description {
    font-size: 13px !important;
  }
}

@media (max-width: 600px) {
  .modern-financials-header {
    padding: 40px 16px !important;
  }

  .header-icon-wrapper {
    width: 64px !important;
    height: 64px !important;
  }

  .modern-financials-title {
    font-size: 28px !important;
  }

  .modern-financials-subtitle {
    font-size: 13px !important;
  }

  .info-card-header {
    flex-direction: column !important;
    text-align: center !important;
    padding: 20px !important;
  }

  .info-icon-wrapper {
    width: 56px !important;
    height: 56px !important;
  }

  .info-title {
    font-size: 18px !important;
  }

  .info-description {
    font-size: 13px !important;
  }

  .stats-card-content {
    padding: 16px !important;
  }

  .stats-icon-wrapper {
    width: 48px !important;
    height: 48px !important;
  }

  .stats-label {
    font-size: 14px !important;
  }

  .stats-sublabel {
    font-size: 11px !important;
  }

  .content-section-header {
    padding: 16px 20px !important;
  }

  .section-icon-badge {
    width: 40px !important;
    height: 40px !important;
  }

  .content-section-title {
    font-size: 18px !important;
  }

  .treeview-wrapper {
    padding: 16px !important;
  }

  .content-wrapper {
    padding: 20px !important;
  }

  .empty-state {
    padding: 40px 16px !important;
  }

  .empty-icon-wrapper {
    width: 100px !important;
    height: 100px !important;
  }

  .empty-title {
    font-size: 18px !important;
  }

  .empty-description {
    font-size: 13px !important;
  }

  .report-card-content {
    padding: 16px !important;
    flex-direction: column !important;
    align-items: flex-start !important;
  }

  .report-icon-wrapper {
    width: 64px !important;
    height: 64px !important;
  }

  .report-year {
    font-size: 16px !important;
  }

  .report-description {
    font-size: 13px !important;
  }

  .report-action {
    width: 100% !important;
  }

  .modern-view-btn {
    width: 100% !important;
    padding: 10px 20px !important;
    font-size: 13px !important;
  }
}
</style>
